import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import PageMeta from "../components/common/PageMeta";
import { userService, User, USER_TYPE_LABELS } from "../services/userService";
import { buildingService, Building } from "../services/buildingService";
import Button from "../components/ui/button/Button";
import { useAuth } from "../context/AuthContext";

type StatusFilter = "active" | "archived";

const INPUT_CLASS =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800";

const SELECT_CLASS =
  "h-11 w-full appearance-none rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 pr-11 text-sm shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800";

const LABEL_CLASS =
  "mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400";

function getInitials(name: string | null | undefined) {
  if (!name || !name.trim()) return "U";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function StatusBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
      Active
    </span>
  ) : (
    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
      Archived
    </span>
  );
}

function UserRow({
  avatar,
  name,
  subtitle,
  tags,
  active,
  toggling,
  onToggle,
}: {
  avatar: string | null;
  name: string;
  subtitle: string;
  tags?: React.ReactNode;
  active: boolean;
  toggling: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50">
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-brand-500 text-sm font-medium text-white">
          {avatar ? (
            <img
              src={avatar}
              alt={name}
              className="h-full w-full object-cover"
            />
          ) : (
            getInitials(name)
          )}
        </div>
        <div>
          <p className="font-medium text-gray-800 dark:text-white">{name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {tags}
        <StatusBadge active={active} />
        <Button
          size="sm"
          variant="outline"
          onClick={onToggle}
          disabled={toggling}
        >
          {toggling ? "..." : active ? "Archive" : "Activate"}
        </Button>
      </div>
    </div>
  );
}

function InviteCodeDisplay({
  code,
  onClose,
}: {
  code: string;
  onClose: () => void;
}) {
  return (
    <div className="text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
        <svg
          className="h-6 w-6 text-green-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>
      <h3 className="mb-4 text-lg font-semibold text-gray-800 dark:text-white">
        User Created
      </h3>
      <div className="mb-6 rounded-lg bg-gray-100 p-4 dark:bg-gray-700">
        <p className="text-3xl font-bold tracking-widest text-gray-800 dark:text-white">
          {code}
        </p>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          6-digit invite code
        </p>
      </div>
      <Button onClick={onClose} className="w-full">
        Done
      </Button>
    </div>
  );
}

function BuildingSelect({
  value,
  onChange,
  buildings,
}: {
  value: string;
  onChange: (v: string) => void;
  buildings: Building[];
}) {
  return (
    <div className="relative">
      <select
        className={`${SELECT_CLASS} ${value ? "text-gray-800 dark:text-white/90" : "text-gray-400"}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" disabled>
          Select building
        </option>
        {buildings.map((b) => (
          <option key={b.id} value={String(b.id)}>
            {b.name}
          </option>
        ))}
      </select>
      <svg
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-700 dark:text-gray-400"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M4.79175 8.02075L10.0001 13.2291L15.2084 8.02075"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  // Data
  const [users, setUsers] = useState<User[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusToast, setStatusToast] = useState<string | null>(null);

  // Per-section filters
  const [adminSearch, setAdminSearch] = useState("");
  const [adminStatus, setAdminStatus] = useState<StatusFilter>("active");
  const [staffSearch, setStaffSearch] = useState("");
  const [staffStatus, setStaffStatus] = useState<StatusFilter>("active");

  // Tab state
  const [activeTab, setActiveTab] = useState<"admins" | "staff">("admins");

  // Action state
  const [togglingUserId, setTogglingUserId] = useState<number | null>(null);

  // Modal state
  const [createModal, setCreateModal] = useState<"admin" | "staff" | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState<string | null>(null);

  // Form state
  const [adminForm, setAdminForm] = useState({
    name: "",
    email: "",
    building_id: "",
  });
  const [staffForm, setStaffForm] = useState({
    name: "",
    email: "",
    building_id: "",
    user_type: "1" as "1" | "2",
  });

  // Redirect non-admins
  useEffect(() => {
    if (currentUser && currentUser.user_type !== 3) {
      navigate("/sites");
    }
  }, [currentUser, navigate]);

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [fetchedUsers, fetchedBuildings] = await Promise.all([
          userService.getAllUsers(),
          buildingService.getBuildings(),
        ]);
        setUsers(fetchedUsers);
        setBuildings(fetchedBuildings);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Derived data
  const admins = users.filter((u) => u.user_type === 3);
  const staff = users.filter((u) => u.user_type === 1 || u.user_type === 2);
  const activeBuildings = buildings.filter((b) => b.status === 1);

  // Filter helpers
  const filterUsers = (
    list: User[],
    search: string,
    status: StatusFilter,
  ) => {
    return list.filter((u) => {
      const q = search.toLowerCase();
      const matchesSearch =
        !search ||
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q)) ||
        (u.username && u.username.toLowerCase().includes(q));
      const matchesStatus =
        status === "active" ? u.user_status === 1 : u.user_status !== 1;
      return matchesSearch && matchesStatus;
    });
  };

  const filteredAdmins = filterUsers(admins, adminSearch, adminStatus);
  const filteredStaff = filterUsers(staff, staffSearch, staffStatus);

  // Status counts
  const adminCounts = {
    active: admins.filter((u) => u.user_status === 1).length,
    archived: admins.filter((u) => u.user_status !== 1).length,
  };
  const staffCounts = {
    active: staff.filter((u) => u.user_status === 1).length,
    archived: staff.filter((u) => u.user_status !== 1).length,
  };

  // Toast
  const showToast = (message: string) => {
    setStatusToast(message);
    setTimeout(() => setStatusToast(null), 3000);
  };

  // Toggle user status
  const handleToggleUserStatus = async (
    userId: number,
    currentStatus: number,
  ) => {
    setTogglingUserId(userId);
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await userService.updateUserStatus(userId, newStatus);
      const fetchedUsers = await userService.getAllUsers();
      setUsers(fetchedUsers);
      showToast(newStatus === 1 ? "User activated" : "User archived");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update user status",
      );
    } finally {
      setTogglingUserId(null);
    }
  };

  // Close modal
  const closeModal = () => {
    setCreateModal(null);
    setModalError(null);
    setInviteCode(null);
    setAdminForm({ name: "", email: "", building_id: "" });
    setStaffForm({ name: "", email: "", building_id: "", user_type: "1" });
  };

  // Create handlers
  const handleCreateAdmin = async () => {
    if (!adminForm.name || !adminForm.email || !adminForm.building_id) {
      setModalError("All fields are required");
      return;
    }
    setIsSubmitting(true);
    setModalError(null);
    try {
      const result = await userService.inviteUser({
        name: adminForm.name,
        email: adminForm.email,
        user_type: 3 as unknown as 1,
        building_id: parseInt(adminForm.building_id),
      });
      setInviteCode(result.invite_code);
      const fetchedUsers = await userService.getAllUsers();
      setUsers(fetchedUsers);
    } catch (err) {
      setModalError(
        err instanceof Error ? err.message : "Failed to create administrator",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateStaff = async () => {
    const isSM = staffForm.user_type === "1";
    if (!staffForm.name || !staffForm.building_id) {
      setModalError("Name and building are required");
      return;
    }
    if (isSM && !staffForm.email) {
      setModalError("Email is required for Site Managers");
      return;
    }
    if (isSM && !staffForm.email.toLowerCase().endsWith("@sbmnyc.com")) {
      setModalError("Site Managers must use an @sbmnyc.com email address");
      return;
    }
    setIsSubmitting(true);
    setModalError(null);
    try {
      const result = await userService.inviteUser({
        name: staffForm.name,
        email: staffForm.email || "",
        user_type: parseInt(staffForm.user_type) as 1 | 2,
        building_id: parseInt(staffForm.building_id),
      });
      setInviteCode(result.invite_code);
      const fetchedUsers = await userService.getAllUsers();
      setUsers(fetchedUsers);
    } catch (err) {
      setModalError(
        err instanceof Error
          ? err.message
          : "Failed to create staff member",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Modal title
  const modalTitle = createModal === "admin" ? "Add Administrator" : "Add Staff Member";

  return (
    <>
      <PageMeta
        title="Settings | SBM Package Admin"
        description="Admin settings and user management"
      />

      {/* Toast */}
      {statusToast && (
        <div className="fixed top-6 right-6 z-50 animate-fade-in-out rounded-lg bg-green-600 px-5 py-3 text-sm font-medium text-white shadow-lg">
          {statusToast}
        </div>
      )}

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-gray-800 dark:text-white">
            Settings
          </h1>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
          </div>
        )}

        {!isLoading && (
          <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
            {/* Tab Bar */}
            <div className="border-b border-gray-200 dark:border-gray-800">
              <nav className="-mb-px flex space-x-2 overflow-x-auto px-5 pt-3 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200 dark:[&::-webkit-scrollbar-thumb]:bg-gray-600 dark:[&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:h-1.5">
                {([
                  { id: "admins" as const, label: "Administrators", count: admins.length },
                  { id: "staff" as const, label: "Staff", count: staff.length },
                ]).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`inline-flex items-center gap-2 border-b-2 px-2.5 py-2 text-sm font-medium transition-colors duration-200 ease-in-out ${
                      activeTab === tab.id
                        ? "border-brand-500 text-brand-500 dark:border-brand-400 dark:text-brand-400"
                        : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    }`}
                  >
                    {tab.label}
                    <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-500 dark:bg-brand-500/15 dark:text-brand-400">
                      {tab.count}
                    </span>
                  </button>
                ))}
              </nav>
            </div>

            {/* Toolbar: Search + Add */}
            <div className="flex flex-col gap-4 border-b border-gray-200 p-5 sm:flex-row sm:items-center sm:justify-between dark:border-gray-800">
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search..."
                  value={activeTab === "admins" ? adminSearch : staffSearch}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (activeTab === "admins") setAdminSearch(v);
                    else setStaffSearch(v);
                  }}
                  className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-4 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white sm:w-56"
                />
              </div>
              <Button
                size="sm"
                onClick={() =>
                  setCreateModal(
                    activeTab === "admins" ? "admin" : "staff",
                  )
                }
              >
                + Add New
              </Button>
            </div>

            {/* Status Toggle */}
            <div className="flex items-center gap-3 border-b border-gray-200 px-5 py-3 dark:border-gray-800">
              <div className="flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
                {(["active", "archived"] as StatusFilter[]).map((s) => {
                  const currentStatus = activeTab === "admins" ? adminStatus : staffStatus;
                  const currentCounts = activeTab === "admins" ? adminCounts : staffCounts;
                  return (
                    <button
                      key={s}
                      onClick={() => {
                        if (activeTab === "admins") setAdminStatus(s);
                        else setStaffStatus(s);
                      }}
                      className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
                        currentStatus === s
                          ? "bg-white text-gray-800 shadow-sm dark:bg-gray-600 dark:text-white"
                          : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
                      }`}
                    >
                      {s} ({currentCounts[s]})
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div className="divide-y divide-gray-200 dark:divide-gray-800">
              {/* Administrators */}
              {activeTab === "admins" && (
                <>
                  {filteredAdmins.map((user) => (
                    <UserRow
                      key={user.id}
                      avatar={user.image}
                      name={user.name || user.username || "Unnamed User"}
                      subtitle={user.email || user.username || ""}
                      active={user.user_status === 1}
                      toggling={togglingUserId === user.id}
                      onToggle={() =>
                        handleToggleUserStatus(user.id, user.user_status)
                      }
                    />
                  ))}
                  {filteredAdmins.length === 0 && (
                    <div className="py-12 text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <p className="mt-4 text-gray-500 dark:text-gray-400">
                        {adminSearch ? "No results match your search" : "No users found"}
                      </p>
                    </div>
                  )}
                </>
              )}

              {/* Staff */}
              {activeTab === "staff" && (
                <>
                  {filteredStaff.map((user) => (
                    <UserRow
                      key={user.id}
                      avatar={user.image}
                      name={user.name || user.username || "Unnamed User"}
                      subtitle={user.email || user.username || ""}
                      tags={
                        <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          {USER_TYPE_LABELS[user.user_type]}
                        </span>
                      }
                      active={user.user_status === 1}
                      toggling={togglingUserId === user.id}
                      onToggle={() =>
                        handleToggleUserStatus(user.id, user.user_status)
                      }
                    />
                  ))}
                  {filteredStaff.length === 0 && (
                    <div className="py-12 text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <p className="mt-4 text-gray-500 dark:text-gray-400">
                        {staffSearch ? "No results match your search" : "No users found"}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ─── Create Modal ─── */}
      {createModal && (
        <div
          className="fixed inset-0 z-999999 flex items-center justify-center bg-black/50"
          onClick={closeModal}
        >
          <div
            className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Invite Code Success View */}
            {inviteCode ? (
              <InviteCodeDisplay code={inviteCode} onClose={closeModal} />
            ) : (
              <>
                <h3 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white">
                  {modalTitle}
                </h3>

                {modalError && (
                  <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                    {modalError}
                  </div>
                )}

                <div className="space-y-4">
                  {/* ── Admin Form ── */}
                  {createModal === "admin" && (
                    <>
                      <div>
                        <label className={LABEL_CLASS}>Name</label>
                        <input
                          type="text"
                          className={INPUT_CLASS}
                          placeholder="Full name"
                          value={adminForm.name}
                          onChange={(e) =>
                            setAdminForm((f) => ({
                              ...f,
                              name: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>Email</label>
                        <input
                          type="email"
                          className={INPUT_CLASS}
                          placeholder="admin@example.com"
                          value={adminForm.email}
                          onChange={(e) =>
                            setAdminForm((f) => ({
                              ...f,
                              email: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>Building</label>
                        <BuildingSelect
                          value={adminForm.building_id}
                          onChange={(v) =>
                            setAdminForm((f) => ({
                              ...f,
                              building_id: v,
                            }))
                          }
                          buildings={activeBuildings}
                        />
                      </div>
                    </>
                  )}

                  {/* ── Staff Form ── */}
                  {createModal === "staff" && (
                    <>
                      <div>
                        <label className={LABEL_CLASS}>Role</label>
                        <div className="flex gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
                          <button
                            type="button"
                            onClick={() =>
                              setStaffForm((f) => ({
                                ...f,
                                user_type: "1",
                                email: "",
                              }))
                            }
                            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                              staffForm.user_type === "1"
                                ? "bg-white text-gray-800 shadow-sm dark:bg-gray-600 dark:text-white"
                                : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
                            }`}
                          >
                            Site Manager
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setStaffForm((f) => ({
                                ...f,
                                user_type: "2",
                                email: "",
                              }))
                            }
                            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                              staffForm.user_type === "2"
                                ? "bg-white text-gray-800 shadow-sm dark:bg-gray-600 dark:text-white"
                                : "text-gray-500 hover:text-gray-700 dark:text-gray-400"
                            }`}
                          >
                            Floor Rep
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className={LABEL_CLASS}>Name</label>
                        <input
                          type="text"
                          className={INPUT_CLASS}
                          placeholder="Full name"
                          value={staffForm.name}
                          onChange={(e) =>
                            setStaffForm((f) => ({
                              ...f,
                              name: e.target.value,
                            }))
                          }
                        />
                      </div>
                      {staffForm.user_type === "1" && (
                        <div>
                          <label className={LABEL_CLASS}>Email</label>
                          <input
                            type="email"
                            className={INPUT_CLASS}
                            placeholder="user@sbmnyc.com"
                            value={staffForm.email}
                            onChange={(e) =>
                              setStaffForm((f) => ({
                                ...f,
                                email: e.target.value,
                              }))
                            }
                          />
                          <p className="mt-1 text-xs text-gray-400">
                            Must be an @sbmnyc.com email
                          </p>
                        </div>
                      )}
                      <div>
                        <label className={LABEL_CLASS}>Building</label>
                        <BuildingSelect
                          value={staffForm.building_id}
                          onChange={(v) =>
                            setStaffForm((f) => ({
                              ...f,
                              building_id: v,
                            }))
                          }
                          buildings={activeBuildings}
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Buttons */}
                <div className="mt-6 flex gap-3">
                  <Button
                    variant="outline"
                    onClick={closeModal}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={createModal === "admin" ? handleCreateAdmin : handleCreateStaff}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    {isSubmitting ? "Creating..." : "Create"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
